import { useOutletContext } from "react-router-dom";
import { Button, List, ListItem, ListItemText } from '@mui/material';

import Page from "~/components/Page";
import ButtonLink from "~/components/ButtonLink";
import { formatNumberToThousands } from "~/utils/utils";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject } from "~/providers/api";
import { IClientProject } from "~/providers/client-interfaces";
import { IOutlet, IPage } from "~/interfaces";
import { IAdminProject } from "~/providers/admin-interfaces";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import PaperWithTitle from '~/components/PaperWithTitle';
import BetterTariffOfferContent from '~/components/BetterTariffOfferContent';
import PaidTypeContent from '~/components/PaidTypeContent';
import PaidConsumptionModal from '~/modals/PaidConsumptionModal';
import AdminChangeBillingMethodModal from '~/modals/AdminChangeBillingMethodModal';
import AdminAdvancedBillingConsentModal from '~/modals/AdminAdvancedBillingConsentModal';
import AdminChangePlanTypeModal from '~/modals/AdminChangePlanTypeModal';
import AdminChangeVIPParamsModal from '~/modals/AdminChangeVIPParamsModal';
import { myUseState } from "~/hooks/myUseState";
import { TProjectBillingMethod } from "~/providers/interfaces";
import { userStore } from "~/stores/user";
import { useTrans } from "~/hooks/useTrans";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	isVisitor: boolean;
	errorMsg: string;
	project: IClientProject | IAdminProject;
	showPaidConsumption: boolean;
	showBillingMethod: boolean;
	showAdvConsent: boolean;
	showVIPParamsChange: boolean;
	changePlanTypeData: {
		switchToProgram: IAdminProject["businessPlanType"];
	};
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	isVisitor: true,
	errorMsg: "",
	project: null,
	showPaidConsumption: false,
	showBillingMethod: false,
	showAdvConsent: false,
	showVIPParamsChange: false,
	changePlanTypeData: null,
};

export default function ProjectFinancialPage({
	isInternal = false,
}: IPage) {
	const { trans, transProjectBusinessPlan, formatCredits, formatPennyPrice, formatPricePerCredit } = useTrans();
	const { lang } = useLang();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const { userData } = userStore(userState => ({
		userData: userState.user,
	}));

	function getVIPPlanTitle() {
		return state.project?.businessPlanType === "vip"
			? trans("projectFinancialPage.businessProgram.vipTurnOff")
			: trans("projectFinancialPage.businessProgram.vipTurnOn");
	}

	function getTechnicalPlanTitle() {
		return state.project?.businessPlanType === "technical"
			? trans("projectFinancialPage.businessProgram.technicalTurnOff")
			: trans("projectFinancialPage.businessProgram.technicalTurnOn");
	}

	function getInternalPlanTitle() {
		return state.project?.businessPlanType === "internal"
			? trans("projectFinancialPage.businessProgram.internalTurnOff")
			: trans("projectFinancialPage.businessProgram.internalTurnOn");
	}

	function setVipPlan() {
		// def. plan je basic
		updateState({
			changePlanTypeData: {
				switchToProgram: state.project.businessPlanType === "vip" ? "basic" : "vip",
			},
		});
	}

	function setTechnicalPlan() {
		// def. plan je basic
		updateState({
			changePlanTypeData: {
				switchToProgram: state.project.businessPlanType === "technical" ? "basic" : "technical",
			},
		});
	}

	function setInternalPlan() {
		// def. plan je basic
		updateState({
			changePlanTypeData: {
				switchToProgram: state.project.businessPlanType === "internal" ? "basic" : "internal",
			},
		});
	}

	function onChangeVIPProgramSave(updatedProject: Partial<IAdminProject>) {
		setState(prev => ({
			...prev,
			isDirty: false,
			showVIPParamsChange: false,
			project: {
				...prev.project,
				...updatedProject,
			},
		}));
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
		]);

		updateState({
			loading: false,
			// castecna data
			...projectData.noErrors
				? {
					project: projectData.data,
					isVisitor: !isInternal && projectData.data.userRole === "visitor",
				}
				: {},
			errorMsg: [
				...projectData.errors,
			].join(", "),
		});
	}

	function onChangePlanTypeSave(lastUpdate: string, businessPlanType: IAdminProject["businessPlanType"]) {
		setState(prev => ({
			...prev,
			changePlanTypeData: null,
			isDirty: false,
			project: {
				...prev.project,
				businessPlanType,
				lastUpdate,
			},
		}));
		// znovu nacteme
		loadData();
	}

	function onAdminChangeBillingMethod(lastUpdate: string, billingMethod: TProjectBillingMethod) {
		setState(prev => ({
			...prev,
			showBillingMethod: false,
			isDirty: false,
			project: {
				...prev.project,
				billingMethod,
				lastUpdate,
			},
		}));
		// znovu nacteme
		loadData();
	}

	function onPaidConsumptionSave(project: Partial<IClientProject | IAdminProject>) {
		setState(prev => ({
			...prev,
			isDirty: false,
			showPaidConsumption: false,
			project: {
				...prev.project,
				...project,
			},
		}));
		// znovu nacteme
		loadData();
	}

	function getSoftConsumptionLimit() {
		if (state.project.softConsumptionLimit === null) {
			return state.project.billedConsumption
				? trans("limits.soft.paid")
				: trans("limits.soft.unpaid");
		}

		return formatCredits(state.project.softConsumptionLimit, true);
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	/* eslint-disable no-magic-numbers */
	return <Page checkLogged={true} title={trans("projectFinancialPage.title")} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, trans("projectFinancialPage.title"))}
		checkInternalRole={isInternal ? "salesman" : "none"} onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.financial">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ state.project && <div className="dashboardGrid">
			{ isInternal && <PaperWithTitle title={ trans("projectFinancialPage.businessProgram.title") } className="financialPage__programPaper">
				<div className="financialPage__programPaperContLeft">
					<div className="financialPage__programPaperContTitle">
						{ trans("projectDashboardPage.program") }: <strong className="margin-left-large">
							{ transProjectBusinessPlan(state.project.businessPlanType) }
						</strong>
					</div>
					{ (state.project.businessPlanType === "basic" || state.project.businessPlanType === "advanced") && <div className="margin-top-large">
						<ButtonLink title={ trans("projectFinancialPage.businessProgram.billingConsent") } onClick={() => updateState({ showAdvConsent: true })} />
					</div> }
				</div>
				<div className="financialPage__programPaperContRight">
					<Button onClick={setVipPlan} color="success" variant="contained">
						{ getVIPPlanTitle() }
					</Button>
					{ userData.internalRole === "internal_admin" && <>
						<Button onClick={setTechnicalPlan} color="success" variant="contained">
							{ getTechnicalPlanTitle() }
						</Button>
						<Button onClick={setInternalPlan} color="success" variant="contained">
							{ getInternalPlanTitle() }
						</Button>
					</> }
				</div>
			</PaperWithTitle> }
			{ !isInternal && <PaperWithTitle title={ trans("projectFinancialPage.tarif.title") } className="financialPage__tarifColumns">
				<div className="financialPage__programPaperContLeft">
					<span className="financialPage__programPaperContTitle">
						{ trans("projectFinancialPage.tarif.title") }: <strong className="margin-left-large">{ transProjectBusinessPlan(state.project.businessPlanType) }</strong>
					</span>
					<List>
						<ListItem sx={{ paddingLeft: 0 }}>
							<ListItemText primary={formatNumberToThousands(state.project.freeCreditsPerMonth)} secondary={trans("projectDashboardPage.freeMonthlyCredits")} />
						</ListItem>
					</List>
				</div>
				<div className="financialPage__tarifColumnsRightColumn">
					<BetterTariffOfferContent params={params} projectData={state.project} onChange={isDirty => updateState({ isDirty })} onSave={() => loadData()} />
				</div>
			</PaperWithTitle> }
			<PaperWithTitle title={ trans("paidConsumptionModal.title") } onClick={state.isVisitor ? null : () => updateState({ showPaidConsumption: true })}>
				<div className="financialPage__billedConsumption">
					<span className="financialPage__programPaperContTitle">
						{ trans("projectFinancialPage.paidConsumption.title") }:
						<strong className="margin-left-large">
							{ state.project.billedConsumption ? trans("projectFinancialPage.paidConsumption.on") : trans("projectFinancialPage.paidConsumption.off") }
						</strong>
					</span>
					<span></span>
				</div>
				<List>
					{ state.project.billedConsumption && <ListItem>
						<ListItemText primary={formatCredits(state.project.totalConsumptionLimit)} secondary={trans("projectFinancialPage.paidConsumption.monthlyLimit")} />
					</ListItem> }
					<ListItem>
						<ListItemText primary={getSoftConsumptionLimit()} secondary={trans("projectFinancialPage.paidConsumption.warning")} />
					</ListItem>
				</List>
			</PaperWithTitle>
			<PaperWithTitle title={ trans("projectFinancialPage.paidType.title") } className="financialPage__paidTypeContent" { ...isInternal ? { onClick: () => updateState({ showBillingMethod: true }) } : {} }>
				<PaidTypeContent projectData={state.project} isVisitor={state.isVisitor} isInternal={isInternal} params={params} onSave={() => loadData()} />
			</PaperWithTitle>
			{ isInternal && state.project.businessPlanType === "vip" && <PaperWithTitle title={ trans("projectFinancialPage.vipProgram.title") } onClick={() => updateState({ showVIPParamsChange: true })}>
				<List>
					<ListItem>
						<ListItemText primary={formatPricePerCredit(state.project.pricePerCredit)} secondary={trans("projectDashboardPage.thousandCreditsPrice")} />
					</ListItem>
					<ListItem>
						<ListItemText primary={formatPennyPrice(state.project.fixedBillingAmount)} secondary={trans("projectDashboardPage.fixedMonthlyPrice")} />
					</ListItem>
					<ListItem>
						<ListItemText primary={formatNumberToThousands(state.project.freeCreditsPerMonth)} secondary={trans("projectDashboardPage.freeMonthlyCredits")} />
					</ListItem>
				</List>
			</PaperWithTitle> }
		</div> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} sx={{ marginTop: "var(--margin-large)" }} /> }
		{ state.showPaidConsumption && <PaidConsumptionModal projectData={state.project} isInternal={isInternal} onClose={() => updateState({ showPaidConsumption: false, isDirty: false })}
			onSave={onPaidConsumptionSave} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.showBillingMethod && <AdminChangeBillingMethodModal projectData={state.project as IAdminProject} onClose={() => updateState({ showBillingMethod: false, isDirty: false })}
			onSave={onAdminChangeBillingMethod} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.showAdvConsent && <AdminAdvancedBillingConsentModal params={params} onClose={() => updateState({ showAdvConsent: false })} /> }
		{ state.changePlanTypeData && <AdminChangePlanTypeModal projectData={state.project as IAdminProject} switchToProgram={state.changePlanTypeData.switchToProgram}
			onClose={() => updateState({ changePlanTypeData: null })} onSave={onChangePlanTypeSave} /> }
		{ state.showVIPParamsChange && <AdminChangeVIPParamsModal projectData={state.project as IAdminProject} onClose={() => updateState({ showVIPParamsChange: false, isDirty: false })}
			onSave={onChangeVIPProgramSave} onChange={isDirty => updateState({ isDirty })} /> }
	</Page>;
}
